/**
 * AI Models Configuration
 * 
 * This file allows you to configure which AI models to use for different tasks.
 * Simply set the 'enabled' property to true for the model you want to use.
 * Only one model should be enabled at a time.
 */

export interface AIModelConfig {
  name: string;
  provider: 'openai' | 'deepseek' | 'openrouter';
  model: string;
  enabled: boolean;
  apiKeyEnvVar: string;
  baseUrl?: string;
  maxTokens: number;
  temperature: number;
}

// Configuration for all available AI models
export const aiModels: AIModelConfig[] = [
  {
    name: 'GPT-4 Turbo',
    provider: 'openai',
    model: 'gpt-4-turbo-preview',
    enabled: false, // Default enabled
    apiKeyEnvVar: 'REACT_APP_OPENAI_API_KEY',
    maxTokens: 4000,
    temperature: 0.7
  },
  {
    name: 'DeepSeek',
    provider: 'deepseek',
    model: 'deepseek-chat',
    enabled: false,
    apiKeyEnvVar: 'REACT_APP_DEEPSEEK_API_KEY',
    baseUrl: 'https://api.deepseek.com/v1/chat/completions',
    maxTokens: 10000,
    temperature: 0.7
  },
  {
    name: 'OpenRouter (GPT-4 Turbo)',
    provider: 'openrouter',
    model: 'openai/gpt-4-turbo-preview',
    enabled: true,
    apiKeyEnvVar: 'REACT_APP_OPENROUTER_API_KEY',
    baseUrl: 'https://openrouter.ai/api/v1/chat/completions',
    maxTokens: 10000,
    temperature: 0.7
  },
  {
    name: 'OpenRouter (GPT-3.5)',
    provider: 'openrouter',
    model: 'openai/gpt-3.5-turbo',
    enabled: false,
    apiKeyEnvVar: 'REACT_APP_OPENROUTER_API_KEY',
    baseUrl: 'https://openrouter.ai/api/v1/chat/completions',
    maxTokens: 10000,
    temperature: 0.7
  },
  {
    name: 'OpenRouter (Claude Sonnet 3.5)',
    provider: 'openrouter',
    model: 'anthropic/claude-3-sonnet-20240229',
    enabled: false,
    apiKeyEnvVar: 'REACT_APP_OPENROUTER_API_KEY',
    baseUrl: 'https://openrouter.ai/api/v1/chat/completions',
    maxTokens: 10000,
    temperature: 0.7
  },
  {
    name: 'OpenRouter (Claude Sonnet 3.7)',
    provider: 'openrouter',
    model: 'anthropic/claude-3-sonnet-20240307',
    enabled: false,
    apiKeyEnvVar: 'REACT_APP_OPENROUTER_API_KEY',
    baseUrl: 'https://openrouter.ai/api/v1/chat/completions',
    maxTokens: 10000,
    temperature: 0.7
  },
  {
    name: 'OpenRouter (ChatGPT 4.5)',
    provider: 'openrouter',
    model: 'openai/gpt-4o',
    enabled: false,
    apiKeyEnvVar: 'REACT_APP_OPENROUTER_API_KEY',
    baseUrl: 'https://openrouter.ai/api/v1/chat/completions',
    maxTokens: 10000,
    temperature: 0.7
  }
];

// Function to get the currently enabled model
export const getEnabledModel = (): AIModelConfig => {
  console.log('Getting enabled model...');
  
  // Log all models and their enabled status
  aiModels.forEach(model => {
    console.log(`Model: ${model.name}, Enabled: ${model.enabled}, Provider: ${model.provider}`);
  });
  
  const enabledModel = aiModels.find(model => model.enabled);
  if (!enabledModel) {
    console.log('No model is enabled, defaulting to the first one');
    // If no model is enabled, default to the first one
    aiModels[0].enabled = true;
    return aiModels[0];
  }
  
  console.log(`Selected model: ${enabledModel.name}, Provider: ${enabledModel.provider}`);
  return enabledModel;
};

// Function to enable a specific model and disable all others
export const enableModel = (modelName: string): AIModelConfig => {
  // Disable all models first
  aiModels.forEach(model => {
    model.enabled = false;
  });
  
  // Enable the specified model
  const modelToEnable = aiModels.find(model => model.name === modelName);
  if (modelToEnable) {
    modelToEnable.enabled = true;
    return modelToEnable;
  }
  
  // If model not found, enable the default
  aiModels[0].enabled = true;
  return aiModels[0];
};